import { cx } from "@jugl-web/utils";
import { FC, PropsWithChildren, ReactNode, useMemo } from "react";
import { Tooltip } from "../../primitives/Tooltip";
import { ReactComponent as HintIcon } from "./assets/hint.svg";

export interface FormGroupProps {
  label?: string;
  isInvalid?: boolean;
  isRequired?: boolean;
  isDisabled?: boolean;
  tooltip?: ReactNode;
  errorMessage?: string;
  labelPosition?: "outside" | "border-overlap";
  classNames?: {
    wrapperClassName?: string;
    labelClassName?: string;
    labelBackgroundClassName?: string;
  };
  labelFor?: string;
  lengthIndicator?: {
    max?: number;
    current?: number;
  };
}

export const FormGroup: FC<PropsWithChildren<FormGroupProps>> = ({
  label,
  isRequired,
  tooltip,
  errorMessage,
  isDisabled,
  labelFor,
  classNames: {
    labelClassName,
    wrapperClassName,
    labelBackgroundClassName,
  } = {},
  children,
  lengthIndicator,
  isInvalid,
  labelPosition = "outside",
}) => {
  const lengthIndicatorText = useMemo(() => {
    const { max, current } = lengthIndicator || {};
    if (max && current && current >= max) {
      return `${current}/${max}`;
    }
    return undefined;
  }, [lengthIndicator]);

  const isBorderOverlap = labelPosition === "border-overlap";

  return (
    <div
      className={cx(
        "relative flow-root",
        label && isBorderOverlap && "mt-0.5",
        wrapperClassName
      )}
    >
      {label && (
        <Tooltip
          placement="right"
          isDisabled={!tooltip}
          className="z-1001 font-secondary text-sm"
          renderTrigger={({ props, ref: tooltipRef }) => (
            <div
              ref={tooltipRef}
              {...props}
              className={cx(
                "group mb-2 inline-flex max-w-full items-center gap-1",
                isBorderOverlap &&
                  "absolute -top-0.5 left-2.5 max-w-[calc(100%-20px)] bg-white px-2",
                labelBackgroundClassName
              )}
              style={
                isBorderOverlap && isDisabled
                  ? {
                      background:
                        "linear-gradient(to bottom, white calc(50% + 1px), #FAFAFA calc(50% + 1px))",
                    }
                  : undefined
              }
            >
              <label
                htmlFor={labelFor}
                className={cx(
                  "flex truncate font-secondary text-dark-700 transition-colors",
                  isBorderOverlap
                    ? "text-xs"
                    : "ml-[10px] border-grey-600 text-[15px] leading-[22px]",
                  tooltip && "group-hover:text-primary-900",
                  tooltip &&
                    !isBorderOverlap &&
                    "border-0 border-b-2 border-dashed",
                  isDisabled && "text-dark-500",
                  labelClassName
                )}
              >
                <span className="truncate">{label}</span>
                {isRequired && (
                  <span className="text-gradients-danger"> *</span>
                )}
              </label>
              {tooltip && (
                <HintIcon
                  className={cx(
                    "shrink-0 text-grey-700 transition-colors group-hover:text-primary-900",
                    isBorderOverlap && "w-[13px]"
                  )}
                />
              )}
            </div>
          )}
        >
          {tooltip}
        </Tooltip>
      )}
      {children}
      {(errorMessage || lengthIndicatorText) && (
        <div className="mt-2 flex items-start justify-between gap-2 font-secondary text-xs leading-2 tracking-[0.14px]">
          {errorMessage ? (
            <div className="flex-1 wrap-break-word text-gradients-danger">
              {errorMessage}
            </div>
          ) : (
            <div />
          )}
          {lengthIndicatorText && (
            <div
              className={cx(
                "whitespace-nowrap",
                isInvalid ? "text-gradients-danger" : "text-grey"
              )}
            >
              {lengthIndicatorText}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
